import { useEffect, type ReactNode } from "react";
import type { SiteDocument, SiteNode, Href } from "@zirro/schema";
import type { Breakpoint, RenderContext, RenderMode } from "./types";
import { themeToCssVars } from "./theme";
import { ensureFonts } from "./fonts";
import { getRegistryEntry, listRegistry } from "./registry";
import { RenderChild, type NavLink } from "./components";

export interface DocumentRendererProps {
  document: SiteDocument;
  /** Page to render or page ID; defaults to home page ('/'). */
  pageId?: string;
  activePage?: { id: string; slug: string; title: string; nodes: SiteNode[] };
  mode: RenderMode;
  /** Editor viewport simulation; default 'desktop' (base props only). */
  breakpoint?: Breakpoint;
  selectedNodeId?: string | null;
  onSelectNode?: (nodeId: string | null) => void;
  hoveredNodeId?: string | null;
  onHoverNode?: (nodeId: string | null) => void;
  inlineEditingNodeId?: string | null;
  onInlineEditChange?: (nodeId: string | null) => void;
  onUpdateNodeProps?: (nodeId: string, props: Record<string, unknown>) => void;
  onNavigate?: (href: Href) => void;
  /** See RenderContext.renderSlot (online store product slots). */
  renderSlot?: (node: SiteNode) => ReactNode | undefined;
  /** See RenderContext.headerActions. */
  headerActions?: ReactNode;
  /** Rendered after the page's nodes, e.g. a store's cart or checkout. */
  pageAddon?: ReactNode;
  className?: string;
}

export function resolvePage(
  document: SiteDocument,
  pageId?: string,
  activePage?: { id: string; slug: string; title: string; nodes: SiteNode[] },
): { id: string; slug: string; title: string; nodes: SiteNode[] } | undefined {
  if (activePage) return activePage;
  if (pageId) return document.pages.find((p) => p.id === pageId);
  return document.pages.find((p) => p.slug === "/") ?? document.pages[0];
}

/**
 * The one render engine (PRD §81–83): identical markup for preview and
 * public mode; edit mode adds selection attributes and placeholders.
 */
export function DocumentRenderer({
  document,
  pageId,
  activePage,
  mode,
  breakpoint = "desktop",
  selectedNodeId,
  onSelectNode,
  hoveredNodeId,
  onHoverNode,
  inlineEditingNodeId,
  onInlineEditChange,
  onUpdateNodeProps,
  onNavigate,
  renderSlot,
  headerActions,
  pageAddon,
  className,
}: DocumentRendererProps) {
  const page = resolvePage(document, pageId, activePage);
  const { headingFont, bodyFont } = document.theme.typography;
  useEffect(() => ensureFonts([headingFont, bodyFont]), [headingFont, bodyFont]);
  const ctx: RenderContext = {
    mode,
    document,
    pageId: page?.id,
    breakpoint,
    selectedNodeId,
    onSelectNode,
    hoveredNodeId,
    onHoverNode,
    inlineEditingNodeId,
    onInlineEditChange,
    onUpdateNodeProps,
    onNavigate,
    renderSlot,
    headerActions,
  };

  const headerLinks = collectNavLinks(document);
  const headerNode: SiteNode = document.header ?? {
    id: "default-header",
    type: "Header",
    props: { logoText: document.meta?.title ?? "My site", links: headerLinks },
  };
  const footerNode: SiteNode = document.footer ?? {
    id: "default-footer",
    type: "Footer",
    props: {
      businessName: document.meta?.title ?? "",
      links: headerLinks,
      copyright: "© {{year}} All rights reserved.",
    },
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (mode !== "edit") return;
    const target = (e.target as HTMLElement).closest("[data-node-id]");
    if (target) {
      e.stopPropagation();
      const nodeId = target.getAttribute("data-node-id");
      if (nodeId) {
        onSelectNode?.(nodeId);
      }
    } else {
      onSelectNode?.(null);
    }
  };

  const handleMouseOver = (e: React.MouseEvent<HTMLDivElement>) => {
    if (mode !== "edit" || !onHoverNode) return;
    const target = (e.target as HTMLElement).closest("[data-node-id]");
    if (target) {
      const nodeId = target.getAttribute("data-node-id");
      onHoverNode(nodeId);
    } else {
      onHoverNode(null);
    }
  };

  return (
    <div
      className={className}
      data-zirro-document
      data-mode={mode}
      data-breakpoint={breakpoint}
      onClick={handleClick}
      onMouseOver={handleMouseOver}
      onMouseLeave={() => onHoverNode?.(null)}
      style={{
        ...themeToCssVars(document.theme),
        background: "var(--zirro-color-background)",
        color: "var(--zirro-color-text)",
        minHeight: "100%",
        position: "relative",
      }}
    >
      <RenderChild node={headerNode} ctx={ctx} />
      <main style={{ minHeight: "200px" }}>
        {page?.nodes.map((node) => (
          <RenderChild key={node.id} node={node} ctx={ctx} />
        ))}
        {pageAddon ?? null}
      </main>
      <RenderChild node={footerNode} ctx={ctx} />
    </div>
  );
}

function collectNavLinks(document: SiteDocument): NavLink[] {
  return document.pages
    .filter((p) => p.showInNav !== false && p.slug !== "/shop" && (p.navLabel || p.title)?.trim().toLowerCase() !== "shop")
    .map((p) => ({
      label: p.navLabel || p.title,
      href: { type: "page" as const, pageId: p.id },
    }));
}

export { getRegistryEntry, listRegistry };
